import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box, Typography, Paper, Grid, FormControl, InputLabel, Select, MenuItem,
    Button, CircularProgress, Alert, Tabs, Tab, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Chip, IconButton, Modal, Fade, Backdrop,
    useTheme, Container, alpha
} from '@mui/material';
import {
    FaChartLine, FaCalendarAlt, FaSpinner, FaExclamationTriangle, FaFileExport,
    FaTable, FaBalanceScale, FaTimes
} from 'react-icons/fa';
import { useCompetence } from '../contexts/CompetenceContext';

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const DemonstrativosPage = () => {
    const theme = useTheme();
    const { globalCompetence } = useCompetence();

    const [viewMode, setViewMode] = useState('consolidated'); // 'consolidated', 'detailed'
    const [activeTab, setActiveTab] = useState(0); // 0: dre, 1: balanco
    // Use global competence or current month as default
    const [competencia, setCompetencia] = useState(globalCompetence || new Date().toISOString().slice(0, 7));

    // DRE State
    const [dreData, setDreData] = useState([]);
    // Balanço State
    const [balancoData, setBalancoData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedCC, setSelectedCC] = useState(null);

    // Sync with global competence
    useEffect(() => {
        if (globalCompetence && globalCompetence !== competencia) {
            setCompetencia(globalCompetence);
        }
    }, [globalCompetence]);

    useEffect(() => {
        if (activeTab === 0) {
            fetchDreData();
        } else if (activeTab === 1) {
            fetchBalancoData();
        }
    }, [competencia, activeTab]);

    const fetchDreData = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${API_URL}/reports/dre`, {
                params: { competencia }
            });
            setDreData(response.data.data || []);
        } catch (err) {
            console.error("Erro ao buscar DRE:", err);
            if (err.response && err.response.status === 404) {
                setDreData([]);
            } else {
                setError("Falha ao carregar o relatório DRE. Verifique se o mês foi processado.");
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchBalancoData = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${API_URL}/reports/balanco`, {
                params: { competencia }
            });
            setBalancoData(response.data.data || null);
        } catch (err) {
            console.error("Erro ao buscar Balanço:", err);
            if (err.response && err.response.status === 404) {
                setBalancoData(null);
            } else {
                setError("Falha ao carregar o Balanço Patrimonial. Verifique se o mês foi processado.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleExportDre = () => {
        if (dreData.length === 0) return;

        const headers = ["Centro de Custo", "Receita Bruta", "(-) Impostos", "(=) Receita Líquida", "(-) Custo Variável", "(=) Margem Contribuição", "(-) Custo Fixo/Despesas", "(=) Resultado Operacional"];
        const csvContent = [
            headers.join(";"),
            ...dreData.map(row => [
                row.centro_custo,
                row.receita_bruta.toFixed(2).replace('.', ','),
                row.impostos.toFixed(2).replace('.', ','),
                row.receita_liquida.toFixed(2).replace('.', ','),
                row.custo_variavel.toFixed(2).replace('.', ','),
                row.margem_contribuicao.toFixed(2).replace('.', ','),
                row.custo_fixo.toFixed(2).replace('.', ','),
                row.resultado.toFixed(2).replace('.', ',')
            ].join(";"))
        ].join("\n");

        const downloadCsv = (content, filename) => {
            const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement("a");
            const url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        };

        downloadCsv(csvContent, `dre_gerencial_${competencia}.csv`);
    };

    const handleExportBalanco = () => {
        if (!balancoData) return;

        const csvRows = [];
        csvRows.push("BALANÇO PATRIMONIAL - " + competencia);
        csvRows.push("");

        csvRows.push("ATIVO;VALOR;PASSIVO + PL;VALOR");

        const maxRows = Math.max(
            balancoData.ativo.length,
            balancoData.passivo.length + balancoData.patrimonio_liquido.length + 2
        );

        const rightSide = [
            ...balancoData.passivo.map(i => ({ label: i.conta, valor: i.valor })),
            { label: "--- PATRIMÔNIO LÍQUIDO ---", valor: null },
            ...balancoData.patrimonio_liquido.map(i => ({ label: i.conta, valor: i.valor }))
        ];

        for (let i = 0; i < maxRows; i++) {
            const left = balancoData.ativo[i] || { conta: "", valor: "" };
            const right = rightSide[i] || { label: "", valor: "" };

            csvRows.push([
                left.conta,
                left.valor !== "" ? left.valor.toFixed(2).replace('.', ',') : "",
                right.label,
                right.valor !== null && right.valor !== "" ? right.valor.toFixed(2).replace('.', ',') : ""
            ].join(";"));
        }

        csvRows.push("");
        csvRows.push([
            "TOTAL ATIVO",
            balancoData.totais.total_ativo.toFixed(2).replace('.', ','),
            "TOTAL PASSIVO + PL",
            balancoData.totais.total_passivo_pl.toFixed(2).replace('.', ',')
        ].join(";"));

        const blob = new Blob([csvRows.join("\n")], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `balanco_patrimonial_${competencia}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Totais DRE
    const totalReceitaBruta = dreData.reduce((acc, curr) => acc + curr.receita_bruta, 0);
    const totalImpostos = dreData.reduce((acc, curr) => acc + curr.impostos, 0);
    const totalReceitaLiquida = dreData.reduce((acc, curr) => acc + curr.receita_liquida, 0);
    const totalCustoVariavel = dreData.reduce((acc, curr) => acc + curr.custo_variavel, 0);
    const totalMargemContribuicao = dreData.reduce((acc, curr) => acc + curr.margem_contribuicao, 0);
    const totalCustoFixo = dreData.reduce((acc, curr) => acc + curr.custo_fixo, 0);
    const totalResultado = dreData.reduce((acc, curr) => acc + curr.resultado, 0);

    // Margens
    const margemBrutaPercent = totalReceitaBruta ? (totalReceitaLiquida / totalReceitaBruta) * 100 : 0;
    const margemContribPercent = totalReceitaLiquida ? (totalMargemContribuicao / totalReceitaLiquida) * 100 : 0;
    const margemLiquidaPercent = totalReceitaLiquida ? (totalResultado / totalReceitaLiquida) * 100 : 0;

    const formatCurrency = (value) => value.toLocaleString('pt-BR', { minimumFractionDigits: 2 });

    const DreRow = ({ label, value, subLabel, subValue, isTotal, isDeduction, highlightColor }) => (
        <Box sx={{ py: 1, borderBottom: isTotal ? '1px solid #e0e0e0' : 'none', bgcolor: highlightColor }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2 }}>
                <Typography variant={isTotal ? "subtitle1" : "body1"} fontWeight={isTotal ? "bold" : "medium"} color={isTotal ? "text.primary" : "text.secondary"} sx={{ textTransform: isTotal ? 'uppercase' : 'none' }}>
                    {label}
                </Typography>
                <Typography variant={isTotal ? "subtitle1" : "body1"} fontWeight="bold" color={isDeduction ? "error.main" : "text.primary"}>
                    {isDeduction ? `(${formatCurrency(value)})` : `R$ ${formatCurrency(value)}`}
                </Typography>
            </Box>
            {subLabel && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 4, mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">{subLabel}</Typography>
                    <Typography variant="caption" color={isDeduction ? "error.light" : "text.secondary"}>
                        {isDeduction ? `(${formatCurrency(subValue)})` : `R$ ${formatCurrency(subValue)}`}
                    </Typography>
                </Box>
            )}
        </Box>
    );

    const renderConsolidatedView = () => (
        <Paper sx={{ maxWidth: 900, mx: 'auto', overflow: 'hidden', borderRadius: 2, bgcolor: 'background.paper' }}>
            <Box sx={{ bgcolor: 'action.hover', px: 4, py: 3, textAlign: 'center', borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h5" fontWeight="bold" textTransform="uppercase" color="text.primary">Demonstração do Resultado do Exercício</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Competência: {competencia}</Typography>
            </Box>

            <Box sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: 2, borderColor: 'grey.300', pb: 1, mb: 2 }}>
                    <Typography variant="caption" fontWeight="bold" color="text.secondary" textTransform="uppercase">Descrição</Typography>
                    <Typography variant="caption" fontWeight="bold" color="text.secondary" textTransform="uppercase">Valor</Typography>
                </Box>

                <DreRow
                    label="Receita Operacional Bruta"
                    value={totalReceitaBruta}
                    subLabel="Vendas de Produtos e Serviços"
                    subValue={totalReceitaBruta}
                />

                <DreRow
                    label="(-) Deduções da Receita Bruta"
                    value={totalImpostos}
                    subLabel="Impostos sobre Vendas"
                    subValue={totalImpostos}
                    isDeduction
                />

                <Box sx={{ my: 2, py: 2, bgcolor: 'action.hover', borderTop: 1, borderBottom: 1, borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold" textTransform="uppercase" color="text.primary">= Receita Operacional Líquida</Typography>
                        <Typography variant="subtitle1" fontWeight="bold" color="text.primary">R$ {formatCurrency(totalReceitaLiquida)}</Typography>
                    </Box>
                </Box>

                <DreRow
                    label="(-) Custos Variáveis"
                    value={totalCustoVariavel}
                    subLabel="Custos dos Produtos/Serviços Vendidos"
                    subValue={totalCustoVariavel}
                    isDeduction
                />

                <Box sx={{ my: 2, py: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), borderTop: 1, borderBottom: 1, borderColor: alpha(theme.palette.primary.main, 0.2) }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2 }}>
                        <Box>
                            <Typography variant="subtitle1" fontWeight="bold" color="primary.main" textTransform="uppercase">= Margem de Contribuição</Typography>
                            <Typography variant="caption" color="primary.dark">{margemContribPercent.toFixed(1)}% da Receita Líquida</Typography>
                        </Box>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary.main">R$ {formatCurrency(totalMargemContribuicao)}</Typography>
                    </Box>
                </Box>

                <DreRow
                    label="(-) Despesas Operacionais / Fixas"
                    value={totalCustoFixo}
                    subLabel="Despesas Administrativas e Gerais"
                    subValue={totalCustoFixo}
                    isDeduction
                />

                <Box sx={{ mt: 3, py: 3, bgcolor: totalResultado >= 0 ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1), borderTop: 2, borderColor: totalResultado >= 0 ? 'success.main' : 'error.main', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 3 }}>
                        <Box>
                            <Typography variant="h6" fontWeight="bold" color={totalResultado >= 0 ? 'success.dark' : 'error.dark'} textTransform="uppercase">
                                = Resultado Operacional (EBITDA)
                            </Typography>
                            <Typography variant="body2" fontWeight="medium" color={totalResultado >= 0 ? 'success.main' : 'error.main'}>
                                {margemLiquidaPercent.toFixed(1)}% de Margem Líquida
                            </Typography>
                        </Box>
                        <Typography variant="h5" fontWeight="bold" color={totalResultado >= 0 ? 'success.dark' : 'error.dark'}>
                            R$ {formatCurrency(totalResultado)}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Paper>
    );

    const renderBalancoView = () => (
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
            <Grid container spacing={3}>
                {/* Ativo */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 0, borderRadius: 2, overflow: 'hidden' }}>
                        <Box sx={{ bgcolor: 'primary.main', color: 'white', p: 2, textAlign: 'center' }}>
                            <Typography variant="h6" fontWeight="bold">ATIVO</Typography>
                        </Box>
                        <TableContainer>
                            <Table size="small">
                                <TableHead sx={{ bgcolor: 'action.hover' }}>
                                    <TableRow>
                                        <TableCell>Conta</TableCell>
                                        <TableCell align="right">Valor</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {balancoData?.ativo.map((item, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell>{item.conta}</TableCell>
                                            <TableCell align="right">R$ {formatCurrency(item.valor)}</TableCell>
                                        </TableRow>
                                    ))}
                                    {(!balancoData || balancoData.ativo.length === 0) && (
                                        <TableRow><TableCell colSpan={2} align="center">Nenhuma conta de ativo encontrada</TableCell></TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between' }}>
                            <Typography fontWeight="bold">TOTAL DO ATIVO</Typography>
                            <Typography fontWeight="bold" color="primary.main">R$ {formatCurrency(balancoData?.totais.total_ativo || 0)}</Typography>
                        </Box>
                    </Paper>
                </Grid>

                {/* Passivo + PL */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 0, borderRadius: 2, overflow: 'hidden' }}>
                        <Box sx={{ bgcolor: 'secondary.main', color: 'white', p: 2, textAlign: 'center' }}>
                            <Typography variant="h6" fontWeight="bold">PASSIVO E PATRIMÔNIO LÍQUIDO</Typography>
                        </Box>
                        <TableContainer>
                            <Table size="small">
                                <TableHead sx={{ bgcolor: 'action.hover' }}>
                                    <TableRow>
                                        <TableCell>Conta</TableCell>
                                        <TableCell align="right">Valor</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow sx={{ bgcolor: alpha(theme.palette.text.secondary, 0.05) }}>
                                        <TableCell colSpan={2} sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>PASSIVO CIRCULANTE / NÃO CIRCULANTE</TableCell>
                                    </TableRow>
                                    {balancoData?.passivo.map((item, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell>{item.conta}</TableCell>
                                            <TableCell align="right">R$ {formatCurrency(item.valor)}</TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow sx={{ bgcolor: alpha(theme.palette.text.secondary, 0.05) }}>
                                        <TableCell colSpan={2} sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>PATRIMÔNIO LÍQUIDO</TableCell>
                                    </TableRow>
                                    {balancoData?.patrimonio_liquido.map((item, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell>{item.conta}</TableCell>
                                            <TableCell align="right">R$ {formatCurrency(item.valor)}</TableCell>
                                        </TableRow>
                                    ))}
                                    {(!balancoData || (balancoData.passivo.length === 0 && balancoData.patrimonio_liquido.length === 0)) && (
                                        <TableRow><TableCell colSpan={2} align="center">Nenhuma conta de passivo/PL encontrada</TableCell></TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box sx={{ p: 2, bgcolor: alpha(theme.palette.secondary.main, 0.05), borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between' }}>
                            <Typography fontWeight="bold">TOTAL DO PASSIVO + PL</Typography>
                            <Typography fontWeight="bold" color="secondary.main">R$ {formatCurrency(balancoData?.totais.total_passivo_pl || 0)}</Typography>
                        </Box>
                    </Paper>

                    {/* Alerta de Equilíbrio */}
                    {balancoData && Math.abs(balancoData.totais.total_ativo - balancoData.totais.total_passivo_pl) > 0.01 && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            O Balanço não está fechado! Diferença: R$ {formatCurrency(Math.abs(balancoData.totais.total_ativo - balancoData.totais.total_passivo_pl))}
                        </Alert>
                    )}
                </Grid>
            </Grid>
        </Box>
    );

    return (
        <Box sx={{ p: 3, backgroundColor: 'background.default', minHeight: '100vh' }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Typography variant="h4" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 2, color: 'text.primary' }}>
                        <FaTable color={theme.palette.primary.main} /> Demonstrativos Contábeis
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Visão consolidada dos resultados financeiros.
                    </Typography>
                </Box>
                <Paper sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 2, mt: { xs: 2, md: 0 }, bgcolor: 'background.paper' }}>
                    <FaCalendarAlt color="gray" />
                    <Typography variant="body2" fontWeight="medium" color="text.primary">Competência:</Typography>
                    <input
                        type="month"
                        value={competencia}
                        onChange={(e) => setCompetencia(e.target.value)}
                        style={{ border: 'none', background: 'transparent', fontWeight: 'bold', color: theme.palette.primary.main, cursor: 'pointer' }}
                    />
                </Paper>
            </Box>

            <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)} sx={{ mb: 4, borderBottom: 1, borderColor: 'divider' }}>
                <Tab label="DRE Gerencial" icon={<FaChartLine />} iconPosition="start" />
                <Tab label="Balanço Patrimonial" icon={<FaBalanceScale />} iconPosition="start" />
            </Tabs>

            {/* --- TAB: DRE --- */}
            {activeTab === 0 && (
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Box sx={{ bgcolor: 'action.hover', p: 0.5, borderRadius: 1 }}>
                            <Button
                                variant={viewMode === 'consolidated' ? 'contained' : 'text'}
                                onClick={() => setViewMode('consolidated')}
                                size="small"
                                sx={{ bgcolor: viewMode === 'consolidated' ? 'background.paper' : 'transparent', color: viewMode === 'consolidated' ? 'primary.main' : 'text.secondary', boxShadow: viewMode === 'consolidated' ? 1 : 0, '&:hover': { bgcolor: viewMode === 'consolidated' ? 'background.paper' : 'action.hover' } }}
                            >
                                Visão Consolidada
                            </Button>
                            <Button
                                variant={viewMode === 'detailed' ? 'contained' : 'text'}
                                onClick={() => setViewMode('detailed')}
                                size="small"
                                sx={{ bgcolor: viewMode === 'detailed' ? 'background.paper' : 'transparent', color: viewMode === 'detailed' ? 'primary.main' : 'text.secondary', boxShadow: viewMode === 'detailed' ? 1 : 0, '&:hover': { bgcolor: viewMode === 'detailed' ? 'background.paper' : 'action.hover' } }}
                            >
                                Visão Detalhada (Por CC)
                            </Button>
                        </Box>

                        <Button
                            variant="contained"
                            color="success"
                            startIcon={<FaFileExport />}
                            onClick={handleExportDre}
                            disabled={dreData.length === 0}
                        >
                            Exportar CSV
                        </Button>
                    </Box>

                    {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                            <CircularProgress />
                        </Box>
                    ) : dreData.length === 0 ? (
                        <Paper sx={{ p: 6, textAlign: 'center', borderStyle: 'dashed', borderColor: 'grey.300' }}>
                            <Typography variant="h6" color="textSecondary">Nenhum dado encontrado para a competência {competencia}.</Typography>
                            <Typography variant="body2" color="textSecondary">Certifique-se de que o mês foi processado na tela de Importação.</Typography>
                        </Paper>
                    ) : (
                        <>
                            {viewMode === 'consolidated' ? (
                                renderConsolidatedView()
                            ) : (
                                <TableContainer component={Paper} sx={{ bgcolor: 'background.paper' }}>
                                    <Table>
                                        <TableHead sx={{ bgcolor: 'action.hover' }}>
                                            <TableRow>
                                                <TableCell>Centro de Custo</TableCell>
                                                <TableCell align="right">Receita Bruta</TableCell>
                                                <TableCell align="right">(-) Impostos</TableCell>
                                                <TableCell align="right">(=) Rec. Líquida</TableCell>
                                                <TableCell align="right">(-) Custo Var.</TableCell>
                                                <TableCell align="right">(=) Margem Contrib.</TableCell>
                                                <TableCell align="right">(-) Custo Fixo</TableCell>
                                                <TableCell align="right">(=) Resultado</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {dreData.map((row, index) => (
                                                <TableRow
                                                    key={index}
                                                    hover
                                                    onClick={() => setSelectedCC(row)}
                                                    sx={{ cursor: 'pointer' }}
                                                >
                                                    <TableCell sx={{ fontWeight: 'medium', color: 'primary.main' }}>{row.centro_custo}</TableCell>
                                                    <TableCell align="right">{formatCurrency(row.receita_bruta)}</TableCell>
                                                    <TableCell align="right" sx={{ color: 'error.main' }}>({formatCurrency(row.impostos)})</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(row.receita_liquida)}</TableCell>
                                                    <TableCell align="right" sx={{ color: 'error.main' }}>({formatCurrency(row.custo_variavel)})</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{formatCurrency(row.margem_contribuicao)}</TableCell>
                                                    <TableCell align="right" sx={{ color: 'error.main' }}>({formatCurrency(row.custo_fixo)})</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 'bold', color: row.resultado >= 0 ? 'success.main' : 'error.main' }}>{formatCurrency(row.resultado)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </>
                    )}
                </Box>
            )}

            {/* --- TAB: BALANCO --- */}
            {activeTab === 1 && (
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                        <Button
                            variant="contained"
                            color="success"
                            startIcon={<FaFileExport />}
                            onClick={handleExportBalanco}
                            disabled={!balancoData}
                        >
                            Exportar CSV
                        </Button>
                    </Box>

                    {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                            <CircularProgress />
                        </Box>
                    ) : !balancoData ? (
                        <Paper sx={{ p: 6, textAlign: 'center', borderStyle: 'dashed', borderColor: 'grey.300' }}>
                            <Typography variant="h6" color="textSecondary">Nenhum dado encontrado para a competência {competencia}.</Typography>
                            <Typography variant="body2" color="textSecondary">Certifique-se de que o balancete contém contas de Ativo/Passivo.</Typography>
                        </Paper>
                    ) : (
                        renderBalancoView()
                    )}
                </Box>
            )}

            {/* Modal de Detalhes DRE */}
            <Modal
                open={!!selectedCC}
                onClose={() => setSelectedCC(null)}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{ timeout: 500 }}
            >
                <Fade in={!!selectedCC}>
                    <Box sx={{
                        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                        width: 400, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 24, overflow: 'hidden'
                    }}>
                        {selectedCC && (
                            <>
                                <Box sx={{ bgcolor: 'primary.main', color: 'white', p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="h6" fontWeight="bold">{selectedCC.centro_custo}</Typography>
                                    <IconButton onClick={() => setSelectedCC(null)} size="small" sx={{ color: 'white' }}><FaTimes /></IconButton>
                                </Box>
                                <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography color="textSecondary">Receita Bruta</Typography>
                                        <Typography fontWeight="bold">R$ {formatCurrency(selectedCC.receita_bruta)}</Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between" color="error.main">
                                        <Typography>(-) Impostos</Typography>
                                        <Typography>(R$ {formatCurrency(selectedCC.impostos)})</Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between" bgcolor="action.hover" p={1} borderRadius={1}>
                                        <Typography fontWeight="bold" color="text.primary">(=) Receita Líquida</Typography>
                                        <Typography fontWeight="bold" color="text.primary">R$ {formatCurrency(selectedCC.receita_liquida)}</Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between" color="error.main">
                                        <Typography>(-) Custos Variáveis</Typography>
                                        <Typography>(R$ {formatCurrency(selectedCC.custo_variavel)})</Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between" bgcolor={alpha(theme.palette.primary.main, 0.1)} p={1} borderRadius={1} color="primary.main">
                                        <Typography fontWeight="bold">(=) Margem Contribuição</Typography>
                                        <Typography fontWeight="bold">R$ {formatCurrency(selectedCC.margem_contribuicao)}</Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between" color="error.main">
                                        <Typography>(-) Custos Fixos</Typography>
                                        <Typography>(R$ {formatCurrency(selectedCC.custo_fixo)})</Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between" bgcolor={selectedCC.resultado >= 0 ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1)} p={2} borderRadius={1} color={selectedCC.resultado >= 0 ? 'success.dark' : 'error.dark'}>
                                        <Typography fontWeight="bold" variant="h6">Resultado</Typography>
                                        <Typography fontWeight="bold" variant="h6">R$ {formatCurrency(selectedCC.resultado)}</Typography>
                                    </Box>
                                </Box>
                            </>
                        )}
                    </Box>
                </Fade>
            </Modal>
        </Box>
    );
};

export default DemonstrativosPage;
